import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, RefreshCw } from 'lucide-react';
import { instanciaService, MetaWebhookStatus } from '../../services/instanciaService';
import { useToast } from '../../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';

interface MetaWebhookDialogProps {
  instanciaId: string;
  open: boolean;
  onClose: () => void;
}

const COR_SITUACAO: Record<MetaWebhookStatus['situacao'], string> = {
  ok: 'border-green-500 bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300',
  padrao: 'border-sky-500 bg-sky-50 text-sky-800 dark:bg-sky-900/20 dark:text-sky-300',
  outro_destino: 'border-red-500 bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300',
  nao_inscrito: 'border-red-500 bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300',
};

const Url: React.FC<{ valor: string | null; vazio: string }> = ({ valor, vazio }) => (
  valor
    ? <code className="break-all text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-800 dark:text-gray-100">{valor}</code>
    : <span className="text-xs text-muted-foreground">{vazio}</span>
);

const MetaWebhookDialog: React.FC<MetaWebhookDialogProps> = ({ instanciaId, open, onClose }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [status, setStatus] = useState<MetaWebhookStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const { data } = await instanciaService.obterWebhookMeta(instanciaId);
      setStatus(data);
    } catch (e: any) {
      setErro(e.response?.data?.message || t('metaWebhook.loadError'));
    } finally {
      setLoading(false);
    }
  }, [instanciaId, t]);

  useEffect(() => {
    if (open) carregar();
  }, [open, carregar]);

  const reinscrever = async () => {
    if (!status) return;
    if (!window.confirm(t('metaWebhook.confirm', { destino: status.esperado || t('metaWebhook.appDefault') }))) return;
    setFixing(true);
    try {
      const { data } = await instanciaService.reinscreverWebhookMeta(instanciaId);
      setStatus(data);
      toast({ title: t('metaWebhook.fixedTitle'), description: t('metaWebhook.fixedDesc') });
    } catch (e: any) {
      toast({ title: t('metaWebhook.fixError'), description: e.response?.data?.message, variant: 'destructive' });
    } finally {
      setFixing(false);
    }
  };

  const precisaAjuste = status && status.situacao !== 'ok';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl dark:bg-slate-700">
        <DialogHeader>
          <DialogTitle className="dark:text-white">{t('metaWebhook.title')}</DialogTitle>
          <DialogDescription className="dark:text-gray-200">{t('metaWebhook.description')}</DialogDescription>
        </DialogHeader>

        {loading && !status ? (
          <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> {t('metaWebhook.loading')}
          </div>
        ) : erro ? (
          <p className="text-sm text-red-600 dark:text-red-400">{erro}</p>
        ) : status && (
          <div className="space-y-3">
            <div className={`rounded-md border-l-4 px-3 py-2 text-sm ${COR_SITUACAO[status.situacao]}`}>
              <p className="font-semibold">{t(`metaWebhook.situacao.${status.situacao}.title`)}</p>
              <p className="mt-0.5">{t(`metaWebhook.situacao.${status.situacao}.desc`)}</p>
            </div>

            <dl className="text-sm space-y-2 dark:text-gray-100">
              <div>
                <dt className="text-muted-foreground">{t('metaWebhook.current')}</dt>
                <dd><Url valor={status.atual} vazio={t('metaWebhook.noOverride')} /></dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t('metaWebhook.expected', { ambiente: status.ambiente.toUpperCase() })}</dt>
                <dd><Url valor={status.esperado} vazio={t('metaWebhook.noExpected')} /></dd>
              </div>
            </dl>

            {precisaAjuste && (
              <p className="text-xs text-muted-foreground">{t('metaWebhook.fixHint')}</p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={carregar} disabled={loading || fixing} className="dark:text-white dark:bg-slate-700">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('metaWebhook.refresh')}
          </Button>
          {precisaAjuste && (
            <Button onClick={reinscrever} disabled={fixing || loading}>
              {fixing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('metaWebhook.fix')}
            </Button>
          )}
          <Button variant="outline" onClick={onClose} className="dark:text-white dark:bg-slate-700">{t('metaWebhook.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MetaWebhookDialog;
